import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Calculator, CheckCircle, XCircle, Star } from 'lucide-react';
import { deliveryFeeService } from '../../services/deliveryFeeService';
import { DeliveryFeeConfig, CALCULATION_METHOD_LABELS } from '../../types/deliveryFee';
import { useToast } from '../../hooks/use-toast';
import DeliveryFeeConfigForm from '../../components/DeliveryFee/DeliveryFeeConfigForm.tsx';
import DeliveryFeeCalculator from '../../components/DeliveryFee/DeliveryFeeCalculator.tsx';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

const DeliveryFeeConfigs: React.FC = () => {
    const [configs, setConfigs] = useState<DeliveryFeeConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showCalculator, setShowCalculator] = useState(false);
    const [editingConfig, setEditingConfig] = useState<DeliveryFeeConfig | null>(null);
    const { toast } = useToast();
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; configId: number | null }>({ open: false, configId: null });
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    useEffect(() => {
        loadConfigs();
    }, []);

    const loadConfigs = async () => {
        try {
            setLoading(true);
            const data = await deliveryFeeService.getAll();
            setConfigs(data);
        } catch (error: any) {
            toast({
                title: 'Erreur',
                description: 'Impossible de charger les configurations',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id: number) => {
        setDeleteDialog({ open: true, configId: id });
    };

    const handleConfirmDelete = async () => {
        if (!deleteDialog.configId) return;

        setIsDeleting(true);
        try {
            await deliveryFeeService.delete(deleteDialog.configId);
            toast({
                title: 'Succès',
                description: 'Configuration supprimée'
            });
            setDeleteDialog({ open: false, configId: null });
            loadConfigs();
        } catch (error: any) {
            toast({
                title: 'Erreur',
                description: error.response?.data?.detail || 'Erreur lors de la suppression',
                variant: 'destructive'
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleEdit = (config: DeliveryFeeConfig) => {
        setEditingConfig(config);
        setShowForm(true);
    };

    const handleFormClose = () => {
        setShowForm(false);
        setEditingConfig(null);
        loadConfigs();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Frais de Livraison</h1>
                        <p className="text-gray-600 dark:text-slate-400 mt-1">Gérez les configurations de calcul des frais de livraison</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowCalculator(true)}
                            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700"
                        >
                            <Calculator className="w-4 h-4" />
                            Calculateur
                        </button>
                        <button
                            onClick={() => setShowForm(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            <Plus className="w-4 h-4" />
                            Nouvelle Configuration
                        </button>
                    </div>
                </div>
            </div>

            {/* Configs List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {configs.map((config) => (
                    <div
                        key={config.id}
                        className={`flex flex-col border rounded-lg p-4 hover:shadow-md transition-shadow bg-white dark:bg-slate-900 ${config.is_default
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                            : 'border-gray-200 dark:border-slate-700'
                            }`}
                    >
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-gray-900 dark:text-slate-100">{config.name}</h3>
                                    {config.is_default && (
                                        <Star className="w-4 h-4 text-blue-600 dark:text-blue-400 fill-blue-600 dark:fill-blue-400" />
                                    )}
                                </div>
                                {config.description && (
                                    <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">{config.description}</p>
                                )}
                            </div>
                            <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${config.is_active
                                ? 'bg-green-100 dark:bg-green-950/30 text-green-800 dark:text-green-300'
                                : 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-300'
                                }`}>
                                {config.is_active ? (
                                    <>
                                        <CheckCircle className="w-3 h-3" />
                                        Active
                                    </>
                                ) : (
                                    <>
                                        <XCircle className="w-3 h-3" />
                                        Inactive
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Method */}
                        <div className="mb-3">
                            <span className="inline-block px-3 py-1 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-full text-sm">
                                {CALCULATION_METHOD_LABELS[config.calculation_method as keyof typeof CALCULATION_METHOD_LABELS]}
                            </span>
                        </div>

                        {/* Details */}
                        <div className="space-y-2 text-sm text-gray-600 dark:text-slate-400 mb-4 flex-grow">
                            <div className="flex justify-between">
                                <span>Frais de base:</span>
                                <span className="font-medium text-gray-900 dark:text-slate-100">{config.base_fee.toFixed(2)}€</span>
                            </div>

                            {config.price_per_km && (
                                <div className="flex justify-between">
                                    <span>Par km:</span>
                                    <span className="font-medium text-gray-900 dark:text-slate-100">{config.price_per_km.toFixed(2)}€/km</span>
                                </div>
                            )}

                            {config.price_per_kg && (
                                <div className="flex justify-between">
                                    <span>Par kg:</span>
                                    <span className="font-medium text-gray-900 dark:text-slate-100">{config.price_per_kg.toFixed(2)}€/kg</span>
                                </div>
                            )}

                            {config.price_per_item && (
                                <div className="flex justify-between">
                                    <span>Par article:</span>
                                    <span className="font-medium text-gray-900 dark:text-slate-100">{config.price_per_item.toFixed(2)}€/article</span>
                                </div>
                            )}

                            {config.free_delivery_threshold && (
                                <div className="flex justify-between">
                                    <span>Livraison gratuite:</span>
                                    <span className="font-medium text-gray-900 dark:text-slate-100">≥ {config.free_delivery_threshold.toFixed(2)}€</span>
                                </div>
                            )}

                            {config.max_delivery_fee && (
                                <div className="flex justify-between">
                                    <span>Plafond max:</span>
                                    <span className="font-medium text-gray-900 dark:text-slate-100">{config.max_delivery_fee.toFixed(2)}€</span>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-3 border-t border-gray-200 dark:border-slate-700 mt-auto">
                            <button
                                onClick={() => handleEdit(config)}
                                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                            >
                                <Edit className="w-4 h-4" />
                                Modifier
                            </button>
                            <button
                                onClick={() => handleDelete(config.id)}
                                className="flex items-center justify-center gap-2 px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {configs.length === 0 && (
                <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-slate-800 rounded-full mb-4">
                        <Calculator className="w-8 h-8 text-gray-400 dark:text-slate-500" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">
                        Aucune configuration
                    </h3>
                    <p className="text-gray-600 dark:text-slate-400 mb-4">
                        Créez votre première configuration de frais de livraison
                    </p>
                    <button
                        onClick={() => setShowForm(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4" />
                        Créer une Configuration
                    </button>
                </div>
            )}

            {/* Form Modal */}
            {showForm && (
                <DeliveryFeeConfigForm
                    config={editingConfig}
                    onClose={handleFormClose}
                />
            )}

            {/* Calculator Modal */}
            {showCalculator && (
                <DeliveryFeeCalculator
                    configs={configs}
                    onClose={() => setShowCalculator(false)}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={deleteDialog.open}
                onOpenChange={(open) => setDeleteDialog({ open, configId: open ? deleteDialog.configId : null })}
                title="Supprimer la configuration"
                description="Êtes-vous sûr de vouloir supprimer cette configuration ? Cette action est irréversible."
                confirmText="Supprimer"
                cancelText="Annuler"
                isLoading={isDeleting}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
};

export default DeliveryFeeConfigs;
